import { configureStore } from '@reduxjs/toolkit';
import authReducer from '../reducers/authSlice';
import mfaReducer from './mfaSlice';

const loadState = () => {
    try {
        // The session stays in localStorage: see authSlice.ts for why sessionStorage was
        // considered and rejected (it would sign every member out on each browser restart without
        // denying an XSS anything, since such an attacker exfiltrates the token the moment it runs).
        // The stale refresh-token copy is dropped though — nothing has ever read it, so leaving a
        // second long-lived credential in storage buys nothing. A browser that ran the interim build
        // has the session in sessionStorage instead; carry it back so nobody is signed out by this
        // change either.
        if (!localStorage.getItem('accessToken')) {
            const interimAccessToken = sessionStorage.getItem('accessToken');
            const interimUser = sessionStorage.getItem('user');
            if (interimAccessToken && interimUser) {
                localStorage.setItem('accessToken', interimAccessToken);
                localStorage.setItem('user', interimUser);
            }
        }
        localStorage.removeItem('refreshToken');

        const accessToken = localStorage.getItem('accessToken');
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        if (accessToken && user) {
            return {
                auth: {
                    accessToken,
                    // Not persisted — see authSlice.ts. Nothing in this app reads
                    // state.auth.refreshToken for any functional purpose today.
                    refreshToken: null,
                    isAuthenticated: true,
                    user,
                },
            };
        }
    } catch (e) {
        console.error('Could not load state', e);
    }
    return undefined;
};

const persistedState = loadState();

export const store = configureStore({
    reducer: {
        auth: authReducer,
        // Deliberately absent from loadState() above: every field of the mfa slice describes ONE
        // token row (t_token.c_mfa_verified), and every login mints fresh rows. Restoring it from
        // localStorage would tell a brand-new session it had already passed its second factor.
        mfa: mfaReducer,
    },
    preloadedState: persistedState,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
